/* eslint-disable /match-exported */
import { GridApiPro } from "@mui/x-data-grid-pro";
import { EntityMappings } from "graphqlBase/__utils__/entityMappings";
import * as React from "react";
import { addNewRowPre } from "./addNewRowPre";
import { DataGridOOFactory } from "./factories/DataGridOOFactory";
import { ExpTableFactory } from "./factories/ExpTableFactory";
import { SubDataListFactory } from "./factories/SubDataListFactory";
import { makeColumnsPre } from "./makeColumnsPre";
import { makeSeverSideFilterPre } from "./makeSeverSideFilterPre";
import { BaseRow, TableType, UseMakeDataGridProps, UseMakeSubDataListProps } from "./types.d";
import { useExecuteQuery } from "./useExecuteQuery";
import { useDataSource } from "./useDataSource";

const useServerSide = <R extends BaseRow, E extends keyof EntityMappings>(
  params: (UseMakeDataGridProps<R, E> | UseMakeSubDataListProps<R, E>) & {
    tableType?: TableType;
    apiRef: React.MutableRefObject<GridApiPro | null>;
    scrollToRowId?: string | undefined;
    selectedRowId?: string | undefined;
    setSelectedRow?: (rowId: string | undefined) => void;
    shouldRefreshList?: boolean | undefined;
    resetShouldRefreshList?: () => void;
    resetScrollToRowId?: () => void;
    setTotalRowsCount?: (count: number) => void;
  }
) => {
  const {
    query,
    variables,
    DataGridComponent,
    tableMode,
    persistance,
    tableId,
    scrollToRowId,
    tableType = "dataGrid",
    apiRef,
    setSelectedRow,
    shouldRefreshList,
    resetShouldRefreshList,
    resetScrollToRowId,
    setTotalRowsCount,
  } = params;

  // Conditionally use infinite scroll or execute query
  const executeQuery = useExecuteQuery({ query, apiRef, variables, enabled: tableType === "dataGrid" });
  const { dataSource, totalRowsCount } = useDataSource({
    query,
    apiRef,
    variables,
    enabled: tableType === "subDataList",
    scrollToRowId,
    setSelectedRow,
    shouldRefreshList,
    resetShouldRefreshList,
    resetScrollToRowId,
    setTotalRowsCount,
  });

  return React.useMemo(() => {
    const DataGridOO = DataGridOOFactory<R, E>({
      apiRef,
      executeQuery,
      DataGridComponent,
      tableMode,
      persistance,
      tableId,
    });

    const SubDataList = SubDataListFactory<R, E>({
      apiRef,
      DataGridComponent,
      tableMode,
      persistance,
      tableId,
    });

    const ExpTable = ExpTableFactory<R, E>({
      apiRef,
      executeQuery,
      DataGridComponent,
      tableMode,
      persistance,
      tableId,
    });

    return {
      DataGridOO,
      SubDataList,
      ExpTable,
      getRowCount: () => totalRowsCount ?? apiRef?.current?.getRowsCount?.() ?? 0,
      useMakeColumns: makeColumnsPre<R, E>({ ...params }),
      makeSeverSideFilter: makeSeverSideFilterPre<R, E>(),
      apiRef,
      addNewRow: addNewRowPre<Partial<EntityMappings[E]["create"]>>(apiRef),
      dataSource,
    };
  }, [executeQuery, tableType, totalRowsCount]);
};

export default useServerSide;
